import React from 'react';
import { ElementCell } from '../../components/nucleus/Shelf';
import { Clock, Zap, Pointer, Workflow, Check } from 'lucide-react';
import { PluginFace as Face } from '../../components/nucleus/faces';
import { plural } from '../../nucleus/format';
import { verifiedWord, triggerKindWord } from './data/words';
import { pluginNote, pluginTone, storeNote, automationNote, standingOf, isToolSchedule } from './data/lists';
import './PluginsSection.css';

// THE SECTION'S CELLS: a plugin is its one face (faces.js PluginFace: its logo,
// else the plugin glyph), an automation its trigger's glyph, a flow the flow
// glyph, all in the section's one hue. A store card says what the plugin
// does (its tools) and carries Set up; an automation's card how it stands.
// A red "!" when an automation wants you; a small square check when ofSelf
// verified a plugin.

// the section's hue: the layer's (external)
export const SECTION_HUE = 'var(--nx-zone-hue, var(--nx-zone-external))';

/** The corner badge an item wears when it wants you (the update badge's red). */
function WantsYou({ note }) {
    return <span className="plugins-wants" title={note} aria-label={note}>!</span>;
}

/** Verified by ofSelf, as a small square mark (no circle). */
export function VerifiedMark({ size = 12 }) {
    const word = verifiedWord(true);
    return (
        <span className="plugins-verified" style={{ width: size, height: size }} title={word} role="img" aria-label={word}>
            <Check size={size - 3} strokeWidth={2.6} aria-hidden="true" />
        </span>
    );
}

// an automation's trigger: on a schedule, when something changes, or only when run
const GLYPH = { schedule: Clock, event: Zap, manual: Pointer };
export function TriggerGlyph({ trigger, size = 30 }) {
    const Icon = GLYPH[trigger] || Pointer;
    return <Icon strokeWidth={1.3} width={size} height={size} />;
}

/** A plugin's face in an inspector head: its logo, else the plugin glyph.
 *  `item` is a pluginItem; `logo` a logo url for a row that has no item. */
export function PluginFace({ item, logo, size = 64 }) {
    return <Face size={size} logo={logo || item?.logo || item?.plugin?.logo_url || null} />;
}

/** An automation's (or a flow's) face: its glyph on the section's hue. */
export function GlyphFace({ children, size = 64 }) {
    return <Face size={size}>{children}</Face>;
}

/** A cell's mark: the one plugin face, at a card's size. */
function CellMark({ logo }) {
    return <span className="plugins-cellface"><Face size={36} logo={logo || null} /></span>;
}

/** How a plugin stands here, in a few words (kept for older callers): its card note. */
export const noteOf = pluginNote;

/** A card's own act, inside the card's press: Set up (a button may not hold a
 *  button, so it is the kit's UpdateBadge pattern: a span that is a button). */
function CardAct({ label, onPress, held = null }) {
    const go = (e) => { e.stopPropagation(); e.preventDefault(); if (!held) onPress(); };
    return (
        <span
            role="button" tabIndex={0} aria-label={label} aria-disabled={held ? true : undefined} title={held || undefined}
            className={`plugins-cardact${held ? ' is-held' : ''}`} onClick={go}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') go(e); }}
        >
            Set up
        </span>
    );
}

/** A plugin as a card. The store: its tools, verified, and Set up. Elsewhere: how it stands. */
export function PluginCell({ item, dim, current, onPress, inStore = false, onSetUp, held = null }) {
    const tone = inStore ? null : pluginTone(item);
    const note = inStore ? storeNote(item) : pluginNote(item);
    return (
        <ElementCell
            className={`plugins-cell${tone ? ` is-${tone}` : ''}${!inStore && item.paused ? ' is-paused' : ''}`}
            hue={SECTION_HUE}
            mark={<CellMark logo={item.logo || item.plugin?.logo_url} />}
            name={inStore && item.verified
                ? <span className="plugins-cellname"><span className="plugins-cellname__word">{item.name}</span><VerifiedMark size={11} /></span>
                : item.name}
            note={note}
            dim={dim}
            current={current}
            title={item.blurb || item.name}
            onPress={onPress}
        >
            {inStore && onSetUp && !item.installed && <CardAct label={`Set up ${item.name}`} onPress={onSetUp} held={held} />}
        </ElementCell>
    );
}

/** An automation's name without its plugin's ("Mailroom · Import messages" → "Import messages"). */
export function shortName(a) {
    const prefix = a.plugin ? `${a.plugin} · ` : null;
    return prefix && a.name.startsWith(prefix) && a.name.length > prefix.length ? a.name.slice(prefix.length) : a.name;
}

/** An automation as a card: its plugin on top, its trigger's glyph, its name, how it stands. */
export function AutomationCell({ item, dim, current, onPress }) {
    const s = standingOf(item);
    const failing = s.state === 'failing';
    const blocked = isToolSchedule(item) && s.tone === 'warn';
    const wants = failing || blocked;
    const note = automationNote(item);
    const tone = failing ? ' is-wants' : blocked ? ' is-warn' : s.tone === 'well' ? ' is-live' : '';
    return (
        <ElementCell
            className={`plugins-cell${tone}`}
            hue={SECTION_HUE}
            tag={item.plugin || undefined}
            badge={wants ? <WantsYou note={s.why || note} /> : null}
            mark={<span className="plugins-glyph"><TriggerGlyph trigger={item.trigger} /></span>}
            name={shortName(item)}
            note={note}
            title={`${item.name} · ${triggerKindWord(item.trigger)}`}
            dim={dim}
            current={current}
            onPress={onPress}
        />
    );
}

export function FlowCell({ flow, dim, current, onPress }) {
    return (
        <ElementCell
            className="plugins-cell"
            hue={SECTION_HUE}
            mark={<span className="plugins-glyph"><Workflow strokeWidth={1.3} /></span>}
            name={flow.name}
            note={plural(flow.steps.length, 'step')}
            title={flow.detail || flow.name}
            dim={dim}
            current={current}
            onPress={onPress}
        />
    );
}

/** A page verb only an admin may press: NucleusPage's Verb, its why said
 *  where it can be read (`heldId` names the visible line that says it). */
export function PageVerb({ icon, children, onClick, primary = false, held = null, heldId }) {
    return (
        <span className="nucleus-verb-cell" title={held || undefined}>
            <button
                type="button"
                className={`nucleus-verb${primary ? ' is-primary' : ''}`}
                onClick={onClick}
                disabled={!!held}
                aria-describedby={held && heldId ? heldId : undefined}
            >
                {icon && <span className="nucleus-verb__glyph" aria-hidden="true">{icon}</span>}
                <span className="nucleus-verb__word">{children}</span>
            </button>
        </span>
    );
}

/** The line that says why a page's verbs are held (an admin's acts, for a member). */
export function HeldLine({ id, children }) {
    if (!children) return null;
    return <p id={id} className="plugins-held">{children}</p>;
}

/** A run's outcome as a square dot (ok done, ink running, warn partial or waiting, faint skipped, red failed). */
export function RunDot({ tone }) {
    return <span className={`plugins-dot is-${tone}`} aria-hidden="true" />;
}
